import { configureStore, combineReducers, type Reducer } from "@reduxjs/toolkit";
import {
  persistReducer,
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";

import appReducer from "./slices/app.slice";
import settingReducer from "./slices/setting.slice";
import authReducer from "./slices/auth.slice";

const rootReducer = combineReducers({
  app: appReducer,
  setting: settingReducer,
  auth: authReducer,
});

/**
 * Persist the whole `auth` slice — including `access_token` — so that a hard
 * page refresh keeps the user logged in, mirroring the React Native app
 * (apps/uninfo/src/services/utils/store.ts), which persists the same slice
 * with no transform.
 *
 * Signing out still clears the session: the `logout` reducer in
 * ./slices/auth.slice resets `loggedIn`, `user_auth`, `access_token` and
 * `push_notification_token`, and the new state is what gets written to
 * localStorage.
 */
const persistConfig = {
  key: "root",
  storage,
  version: 2,
  whitelist: ["setting", "auth"],
};

const persistedReducer = persistReducer(
  persistConfig,
  rootReducer as Reducer<ReturnType<typeof rootReducer>>,
);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) => {
    const middleware = getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    });
    return middleware;
  },
});

const persistor = persistStore(store);

export { store, persistor };

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;